import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  accountingChartsApi,
  bankCashApi,
  type BankCashAccountDto,
  type BankCashTransactionDto,
} from '../../services/api';

const CURRENCY = 'PHP';
const CURRENCY_SYMBOL = '₱';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-PH', { style: 'currency', currency: CURRENCY }).format(value);
}

function formatDisplayDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDateForInput(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDateForExport(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-PH', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function escapeCsv(value: string): string {
  const s = String(value ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

type BankAccountSlot = 'checking' | 'savings' | 'petty';

interface BankAccountCard {
  slot: BankAccountSlot;
  label: string;
  sublabel?: string;
  iconBg: string;
  account: BankCashAccountDto | null;
}

function mapAccountsToCards(accounts: BankCashAccountDto[]): BankAccountCard[] {
  const lower = (s: string) => (s || '').toLowerCase();
  let checking: BankCashAccountDto | null = null;
  let savings: BankCashAccountDto | null = null;
  let petty: BankCashAccountDto | null = null;

  for (const a of accounts) {
    const name = lower(a.name);
    const code = lower(a.code);
    if (!checking && (name.includes('checking') || (name.includes('main') && !name.includes('petty')) || code === '1001')) {
      checking = a;
    } else if (!savings && (name.includes('savings') || code === '1002')) {
      savings = a;
    } else if (!petty && (name.includes('petty') || (name.includes('cash') && !name.includes('checking')) || code === '1010')) {
      petty = a;
    }
  }

  const usedIds = new Set([checking?.id, savings?.id, petty?.id].filter(Boolean));
  const available = accounts.filter((a) => !usedIds.has(a.id));
  if (!checking && available[0]) checking = available[0];
  if (!savings && available[1]) savings = available[1];
  if (!petty && available[2]) petty = available[2];

  return [
    { slot: 'checking', label: 'Main Checking', account: checking, iconBg: 'bg-blue-100 text-blue-600' },
    { slot: 'savings', label: 'Savings Account', account: savings, iconBg: 'bg-violet-100 text-violet-600' },
    { slot: 'petty', label: 'Petty Cash', sublabel: 'Cash on hand', account: petty, iconBg: 'bg-amber-100 text-amber-600' },
  ];
}

interface AddTransactionModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { accountId: string; type: 'deposit' | 'withdrawal'; amount: number; description: string; date: string; files: File[] }) => Promise<boolean>;
  onEdit?: (id: string, data: { accountId: string; type: 'deposit' | 'withdrawal'; amount: number; description: string; date: string; files: File[] }) => Promise<boolean>;
  bankCards: BankAccountCard[];
  submitting: boolean;
  editId?: string | null;
}

function AddTransactionModal({ open, onClose, onSubmit, onEdit, bankCards, submitting, editId }: AddTransactionModalProps) {
  const [accountId, setAccountId] = useState('');
  const [type, setType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(formatDateForInput(new Date()));
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const isEdit = Boolean(editId && onEdit);

  const bankAccountOptions = useMemo(() => bankCards, [bankCards]);

  useEffect(() => {
    if (open) {
      if (isEdit && editId) {
        bankCashApi.getTransaction(editId).then((res) => {
          if (res.data) {
            setAccountId(res.data.accountId);
            setType(res.data.type);
            setAmount(String(res.data.amount));
            setDescription(res.data.description || '');
            setDate(res.data.date?.slice(0, 10) || formatDateForInput(new Date()));
          }
        });
      } else {
        const first = bankAccountOptions.find((c) => c.account);
        setAccountId(first?.account?.id ?? '');
        setType('deposit');
        setAmount('');
        setDescription('');
        setDate(formatDateForInput(new Date()));
        setFiles([]);
      }
    }
  }, [open, bankAccountOptions, isEdit, editId]);

  const amountNum = parseFloat(amount) || 0;
  const isValid = accountId && amountNum > 0 && description.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    const data = { accountId, type, amount: amountNum, description: description.trim(), date, files };
    const success = isEdit && editId && onEdit ? await onEdit(editId, data) : await onSubmit(data);
    if (success) onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div className="relative bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-md">
        <div className="border-b border-gray-100 px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">{isEdit ? 'Edit Transaction' : 'Add Transaction'}</h2>
          <button type="button" onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Account</label>
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className="w-full rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2.5 text-sm text-gray-900 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none" required>
              <option value="">Select account</option>
              {bankAccountOptions.map((card) =>
                card.account ? (
                  <option key={card.account.id} value={card.account.id}>{card.label}</option>
                ) : (
                  <option key={card.slot} value="" disabled>{card.label} (Not set up)</option>
                )
              )}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Type</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setType('deposit')} className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-colors ${type === 'deposit' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                Add (Deposit)
              </button>
              <button type="button" onClick={() => setType('withdrawal')} className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-colors ${type === 'withdrawal' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                Deduct (Withdrawal)
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{type === 'deposit' ? 'Amount to add' : 'Amount to deduct'}</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">{CURRENCY_SYMBOL}</span>
              <input type="number" min={0} step={0.01} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="w-full rounded-lg border border-gray-200 bg-gray-50/80 pl-8 pr-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none tabular-nums" required />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g., Payment from ABC Corp, Office Supplies" className="w-full rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2.5 text-sm text-gray-900 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Upload documents (proof)</label>
            <div className="border border-gray-200 rounded-lg border-dashed bg-gray-50/50 hover:bg-gray-50 transition-colors">
              <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => { const s = e.target.files; if (s) setFiles(Array.from(s)); }} className="hidden" />
              <button type="button" onClick={() => fileInputRef.current?.click()} className="w-full px-4 py-3 flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-900">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                Choose files (receipt, invoice, etc.)
              </button>
              {files.length > 0 && (
                <ul className="px-4 pb-3 space-y-2">
                  {files.map((f, i) => (
                    <li key={i} className="flex items-center justify-between text-sm text-gray-700 bg-white rounded px-3 py-2 border border-gray-100">
                      <span className="truncate">{f.name}</span>
                      <button type="button" onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} className="p-1 text-gray-400 hover:text-red-600" aria-label="Remove">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <p className="mt-1.5 text-xs text-gray-500">Optional. Attach receipts or supporting documents as proof.</p>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={submitting} className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50">Cancel</button>
            <button type="submit" disabled={!isValid || submitting} className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? (isEdit ? 'Saving…' : 'Adding…') : (isEdit ? 'Save Changes' : 'Add Transaction')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface ViewTransactionModalProps {
  open: boolean;
  transactionId: string | null;
  onClose: () => void;
  bankCards: BankAccountCard[];
}

function ViewTransactionModal({ open, transactionId, onClose, bankCards }: ViewTransactionModalProps) {
  const [transaction, setTransaction] = useState<BankCashTransactionDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && transactionId) {
      setLoading(true);
      bankCashApi.getTransaction(transactionId).then((res) => {
        setLoading(false);
        if (res.data) setTransaction(res.data);
      });
    } else {
      setTransaction(null);
    }
  }, [open, transactionId]);

  const accountLabel = transaction ? bankCards.find((c) => c.account && c.account.id === transaction.accountId)?.label ?? transaction.accountName : '';

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div className="relative bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-md">
        <div className="border-b border-gray-100 px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">View Transaction</h2>
          <button type="button" onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-5 space-y-4">
          {loading ? (
            <p className="text-gray-500 text-sm">Loading…</p>
          ) : transaction ? (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Reference</span><p className="font-medium text-gray-900">{transaction.reference}</p></div>
                <div><span className="text-gray-500">Date</span><p className="font-medium text-gray-900">{formatDisplayDate(transaction.date)}</p></div>
                <div className="col-span-2"><span className="text-gray-500">Account</span><p className="font-medium text-gray-900">{accountLabel || transaction.accountName}</p></div>
                <div className="col-span-2"><span className="text-gray-500">Type</span><p className="font-medium text-gray-900">{transaction.type === 'deposit' ? 'Deposit' : 'Withdrawal'}</p></div>
                <div className="col-span-2"><span className="text-gray-500">Amount</span><p className={`font-medium ${transaction.type === 'deposit' ? 'text-emerald-600' : 'text-red-600'}`}>{transaction.type === 'deposit' ? '+' : '-'}{formatCurrency(transaction.amount)}</p></div>
                <div className="col-span-2"><span className="text-gray-500">Description</span><p className="font-medium text-gray-900">{transaction.description || '—'}</p></div>
              </div>
              {transaction.documents && transaction.documents.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Documents</div>
                  <ul className="space-y-2">
                    {transaction.documents.map((doc) => (
                      <li key={doc.id} className="flex items-center justify-between text-sm bg-gray-50 rounded px-3 py-2 border border-gray-100">
                        <span className="truncate text-gray-700">{doc.name}</span>
                        <button type="button" onClick={() => bankCashApi.downloadDocument(doc.id, doc.name)} className="text-brand-600 hover:text-brand-700 font-medium shrink-0 ml-2">
                          Download
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-500 text-sm">Transaction not found.</p>
          )}
        </div>
        <div className="shrink-0 border-t border-gray-100 px-5 py-4">
          <button type="button" onClick={onClose} className="w-full px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200">Close</button>
        </div>
      </div>
    </div>
  );
}

const SETUP_DEFAULTS: Record<BankAccountSlot, { code: string; name: string; description: string }> = {
  checking: { code: '1001', name: 'Main Checking', description: '' },
  savings: { code: '1002', name: 'Savings Account', description: '' },
  petty: { code: '1010', name: 'Petty Cash', description: 'Cash on hand' },
};

interface SetupAccountModalProps {
  open: boolean;
  onClose: () => void;
  slot: BankAccountSlot | null;
  editAccount: BankCashAccountDto | null;
  onSuccess: () => void;
  submitting: boolean;
  setSubmitting: (v: boolean) => void;
  setError: (v: string | null) => void;
}

function SetupAccountModal({ open, onClose, slot, editAccount, onSuccess, submitting, setSubmitting, setError }: SetupAccountModalProps) {
  const isEdit = Boolean(editAccount);
  const defaults = slot ? SETUP_DEFAULTS[slot] : { code: '', name: '', description: '' };
  const [code, setCode] = useState(defaults.code);
  const [name, setName] = useState(defaults.name);
  const [description, setDescription] = useState(defaults.description);

  React.useEffect(() => {
    if (open) {
      if (editAccount) {
        setCode(editAccount.code || '');
        setName(editAccount.name || '');
        setDescription(editAccount.description || '');
      } else if (slot) {
        const d = SETUP_DEFAULTS[slot];
        setCode(d.code);
        setName(d.name);
        setDescription(d.description);
      }
    }
  }, [open, slot, editAccount]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && editAccount) {
        const res = await accountingChartsApi.update(editAccount.id, {
          code: code.trim(),
          name: name.trim(),
          type: 'Asset',
          subtype: '',
          description: description.trim() || '',
        });
        if (res.data) {
          onSuccess();
          onClose();
        } else {
          setError(res.message || res.errors?.code?.[0] || 'Failed to update account.');
        }
      } else {
        const res = await accountingChartsApi.create({
          code: code.trim(),
          name: name.trim(),
          type: 'Asset',
          subtype: '',
          description: description.trim() || '',
        });
        if (res.data) {
          onSuccess();
          onClose();
        } else {
          setError(res.message || res.errors?.code?.[0] || 'Failed to create account.');
        }
      }
    } catch {
      setError(isEdit ? 'Failed to update account.' : 'Failed to create account.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  const slotLabel = slot === 'checking' ? 'Main Checking' : slot === 'savings' ? 'Savings Account' : 'Petty Cash';
  const title = isEdit ? `Edit ${editAccount?.name || slotLabel}` : `Set up ${slotLabel}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div className="relative bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-md">
        <div className="border-b border-gray-100 px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button type="button" onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <p className="text-sm text-gray-600">{isEdit ? 'Update this account in your Chart of Accounts.' : 'Create this account in your Chart of Accounts to track transactions here.'}</p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Account Code</label>
            <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. 1001" className="w-full rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Main Checking" className="w-full rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Cash on hand" className="w-full rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none" />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={submitting} className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50">Cancel</button>
            <button type="submit" disabled={!code.trim() || !name.trim() || submitting} className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save Changes' : 'Create Account')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function BankCashPage() {
  const [accounts, setAccounts] = useState<BankCashAccountDto[]>([]);
  const [transactions, setTransactions] = useState<BankCashTransactionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [viewTransactionId, setViewTransactionId] = useState<string | null>(null);
  const [editTransactionId, setEditTransactionId] = useState<string | null>(null);
  const [setupSlot, setSetupSlot] = useState<BankAccountSlot | null>(null);
  const [editAccount, setEditAccount] = useState<BankCashAccountDto | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bankCards = useMemo(() => mapAccountsToCards(accounts), [accounts]);
  const recentTransactions = useMemo(() => [...transactions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 10), [transactions]);

  const loadData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const [accountsRes, transactionsRes] = await Promise.all([
        bankCashApi.getAccounts(),
        bankCashApi.getTransactions(),
      ]);
      if (accountsRes.data) setAccounts(accountsRes.data);
      if (transactionsRes.data) setTransactions(transactionsRes.data);
    } catch {
      setError('Failed to load data.');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddTransaction = async (data: { accountId: string; type: 'deposit' | 'withdrawal'; amount: number; description: string; date: string; files: File[] }) => {
    setSubmitting(true);
    try {
      const res = await bankCashApi.createTransaction({
        account_id: data.accountId,
        type: data.type,
        amount: data.amount,
        description: data.description,
        date: data.date,
      });
      if (res.data) {
        for (const file of data.files) {
          await bankCashApi.uploadDocument(res.data.id, file);
        }
        await loadData(false);
        setError(null);
        setAddModalOpen(false);
        return true;
      }
      if (res.message) setError(res.message);
      return false;
    } catch {
      setError('Failed to add transaction.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTransaction = async (id: string, data: { accountId: string; type: 'deposit' | 'withdrawal'; amount: number; description: string; date: string; files: File[] }) => {
    setSubmitting(true);
    try {
      const res = await bankCashApi.updateTransaction(id, {
        account_id: data.accountId,
        type: data.type,
        amount: data.amount,
        description: data.description,
        date: data.date,
      });
      if (res.data) {
        for (const file of data.files) {
          await bankCashApi.uploadDocument(id, file);
        }
        await loadData(false);
        setError(null);
        setEditTransactionId(null);
        return true;
      }
      if (res.message) setError(res.message);
      return false;
    } catch {
      setError('Failed to update transaction.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTransaction = async (id: string) => {
    if (!window.confirm('Delete this transaction? This cannot be undone.')) return;
    setSubmitting(true);
    try {
      const res = await bankCashApi.deleteTransaction(id);
      if (res.success) {
        await loadData(false);
        setError(null);
      } else if (res.message) {
        setError(res.message);
      }
    } catch {
      setError('Failed to delete transaction.');
    } finally {
      setSubmitting(false);
    }
  };

  const hasBankAccounts = bankCards.some((c) => c.account);

  const handleExportTransactions = useCallback(() => {
    const sorted = [...transactions].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const headers = ['Account', 'Description', 'Date', 'Reference', 'Type', 'Deposit', 'Withdrawal'];
    const rows = sorted.map((t) => {
      const accountLabel = bankCards.find((c) => c.account && c.account.id === t.accountId)?.label ?? t.accountName ?? t.accountCode ?? '—';
      const deposit = t.type === 'deposit' ? String(t.amount) : '';
      const withdrawal = t.type === 'withdrawal' ? String(t.amount) : '';
      return [
        escapeCsv(accountLabel),
        escapeCsv(t.description || ''),
        escapeCsv(formatDateForExport(t.date)),
        escapeCsv(t.reference || ''),
        escapeCsv(t.type),
        escapeCsv(deposit),
        escapeCsv(withdrawal),
      ].join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bank-cash-transactions-${formatDateForInput(new Date())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [transactions, bankCards]);

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between gap-4 mb-4 shrink-0">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Bank & Cash</h1>
          <p className="text-sm text-gray-500">Manage bank accounts and reconciliation</p>
        </div>
        {(hasBankAccounts || transactions.length > 0) && (
          <div className="flex items-center gap-2">
            {transactions.length > 0 && (
              <button onClick={handleExportTransactions} disabled={loading} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 shadow-card">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export
              </button>
            )}
            {hasBankAccounts && (
              <button onClick={() => setAddModalOpen(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 shadow-card">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Transaction
              </button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="flex-1 flex items-center justify-center bg-white rounded-xl shadow-card border border-gray-100">
          <div className="text-gray-500 text-sm">Loading…</div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-auto space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {bankCards.map((card) => (
              <div key={card.slot} className="bg-white rounded-xl p-5 shadow-card border border-gray-100 hover:shadow-card-hover transition-shadow">
                <div className="flex items-start justify-between">
                  <div className={`p-2.5 rounded-lg ${card.iconBg}`}>
                    {card.slot === 'checking' && <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
                    {card.slot === 'savings' && <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>}
                    {card.slot === 'petty' && <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                  </div>
                  <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">{card.account ? 'Active' : 'Not set up'}</span>
                </div>
                <h3 className="mt-4 text-sm font-semibold text-gray-900">{card.label}</h3>
                {card.sublabel && <p className="text-xs text-gray-500 mt-0.5">{card.sublabel}</p>}
                <p className="mt-2 text-xl font-bold text-gray-900 tabular-nums">{card.account ? formatCurrency(Number(card.account.balance) || 0) : formatCurrency(0)}</p>
                {card.account?.code && <p className="mt-1 text-xs text-gray-500">****{String(card.account.code).slice(-4)}</p>}
                {!card.account ? (
                  <button type="button" onClick={() => { setSetupSlot(card.slot); setEditAccount(null); }} disabled={submitting} className="mt-3 w-full py-2 px-3 rounded-lg text-sm font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 border border-brand-200 disabled:opacity-50">
                    Set up
                  </button>
                ) : (
                  <button type="button" onClick={() => { setEditAccount(card.account!); setSetupSlot(null); }} disabled={submitting} className="mt-3 w-full py-2 px-3 rounded-lg text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 border border-gray-200 disabled:opacity-50">
                    Edit
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl shadow-card border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Recent Transactions</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/80">
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">Account</th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">Description</th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">Date</th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">Reference</th>
                    <th className="text-right px-5 py-3 font-semibold text-gray-600">Deposit</th>
                    <th className="text-right px-5 py-3 font-semibold text-gray-600">Withdrawal</th>
                    <th className="text-center px-5 py-3 font-semibold text-gray-600 w-32">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {recentTransactions.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-5 py-8 text-center text-gray-500">No recent transactions. Add one using the button above.</td>
                    </tr>
                  ) : (
                    recentTransactions.map((t) => {
                      const accountLabel = bankCards.find((c) => c.account && c.account.id === t.accountId)?.label ?? t.accountName;
                      return (
                        <tr key={t.id} className="hover:bg-gray-50/50">
                          <td className="px-5 py-3 font-medium text-gray-900">{accountLabel}</td>
                          <td className="px-5 py-3 text-gray-700 truncate max-w-[200px]" title={t.description || ''}>{t.description || '—'}</td>
                          <td className="px-5 py-3 text-gray-600">{formatDisplayDate(t.date)}</td>
                          <td className="px-5 py-3 text-gray-600">{t.reference}</td>
                          <td className="px-5 py-3 text-right tabular-nums text-emerald-600 font-medium">{t.type === 'deposit' ? `+${formatCurrency(t.amount)}` : '—'}</td>
                          <td className="px-5 py-3 text-right tabular-nums text-red-600 font-medium">{t.type === 'withdrawal' ? `-${formatCurrency(t.amount)}` : '—'}</td>
                          <td className="px-5 py-3">
                            <div className="flex items-center justify-center gap-1">
                              <button type="button" onClick={() => setViewTransactionId(t.id)} disabled={submitting} className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-50" title="View" aria-label="View">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                              </button>
                              <button type="button" onClick={() => setEditTransactionId(t.id)} disabled={submitting} className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-50" title="Edit" aria-label="Edit">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                              </button>
                              <button type="button" onClick={() => handleDeleteTransaction(t.id)} disabled={submitting} className="p-2 rounded-lg text-gray-600 hover:text-red-600 hover:bg-red-50 disabled:opacity-50" title="Delete" aria-label="Delete">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {!hasBankAccounts && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-800">
              <p className="font-medium">Set up bank accounts</p>
              <p className="mt-1 text-amber-700">Click <strong>Set up</strong> on any card above to create Main Checking, Savings, or Petty Cash.</p>
            </div>
          )}
        </div>
      )}

      <AddTransactionModal
        open={addModalOpen || Boolean(editTransactionId)}
        onClose={() => { setAddModalOpen(false); setEditTransactionId(null); }}
        onSubmit={handleAddTransaction}
        onEdit={handleEditTransaction}
        bankCards={bankCards}
        submitting={submitting}
        editId={editTransactionId}
      />
      <ViewTransactionModal open={Boolean(viewTransactionId)} transactionId={viewTransactionId} onClose={() => setViewTransactionId(null)} bankCards={bankCards} />
      <SetupAccountModal open={Boolean(setupSlot || editAccount)} onClose={() => { setSetupSlot(null); setEditAccount(null); }} slot={setupSlot} editAccount={editAccount} onSuccess={() => loadData(false)} submitting={submitting} setSubmitting={setSubmitting} setError={setError} />
    </div>
  );
}
